/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var setupGetInstanceId = require('../tools/setupGetInstanceId.js');
var environment = require('./environment.js');
var useIdPrefix = require('./useIdPrefix.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);

// This file was heavily inspired by Reach UI and their work on their auto-id
const getId = setupGetInstanceId["default"]();
const useIsomorphicLayoutEffect = environment.canUseDOM ? React.useLayoutEffect : React.useEffect;
let serverHandoffCompleted = false;

/**
 * Generate a unique ID with an optional prefix prepended to it
 * @param {string} [prefix]
 * @returns {string}
 */
function useId() {
  let prefix = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'id';
  const _prefix = useIdPrefix.useIdPrefix();
  const [id, setId] = React.useState(() => {
    if (serverHandoffCompleted) {
      return `${_prefix ? `${_prefix}-` : ``}${prefix}-${getId()}`;
    }
    return null;
  });
  useIsomorphicLayoutEffect(() => {
    if (id === null) {
      setId(`${_prefix ? `${_prefix}-` : ``}${prefix}-${getId()}`);
    }
  }, [getId]);
  React.useEffect(() => {
    if (serverHandoffCompleted === false) {
      serverHandoffCompleted = true;
    }
  }, []);
  if (typeof React__default["default"]['useId'] === 'function') {
    const id = nativeReactUseId(_prefix, prefix);
    return id;
  }
  return id;
}
function nativeReactUseId(_prefix, prefix) {
  const getId = React__default["default"]['useId']();
  const id = `${_prefix ? `${_prefix}-` : ``}${prefix}-${getId}`;
  return id;
}

/**
 * Generate a unique id if a given `id` is not provided
 * @param {string|undefined} id
 * @returns {string}
 */
function useFallbackId(id) {
  const fallback = useId();
  return id ?? fallback;
}

exports.useFallbackId = useFallbackId;
exports.useId = useId;
